import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {OverlayPanel} from 'primeng/overlaypanel';
import {MessageService} from 'primeng/api';
import {EventEnum} from '@app/enums/event.enum';
import {
    NotificacionWeb,
    NotificacionWebSocket,
    TipoNotificacionWeb,
    TipoNotificacionWebOption
} from '@app/domain/notificacion-web';
import {NotificacionWebService} from '@app/service/notificacion-web.service';
import {SocketIoService} from '@app/service/socket-io.service';
import {NotificacionWebActionService} from '@app/service/notificacion-web-action.service';
import {BrowserNotificationService} from '@app/service/browser-notification.service';
import {ListasService} from '@app/service/listas.service';

@Component({
    selector: 'app-notification-indicator',
    templateUrl: './notification-indicator.component.html',
    styleUrls: ['./notification-indicator.component.scss']
})
export class NotificationIndicatorComponent implements OnInit, OnDestroy {

    @ViewChild('recentPanel') recentPanel: OverlayPanel;

    readonly recentPageSize = 10;
    readonly historyPageSize = 10;
    readonly requireUnreadNotificationToExecute = true;
    private readonly notificationSoundUrl = 'assets/resources/sounds/swiftly.mp3';

    recentNotifications: NotificacionWeb[] = [];
    historyNotifications: NotificacionWeb[] = [];
    historyTotal = 0;
    historyPage = 0;
    historyVisible = false;
    historyNotificationType: TipoNotificacionWeb;
    historyNotificationTypes: TipoNotificacionWebOption[] = [];
    historyDateFrom: Date;
    historyDateTo: Date;
    loadingRecent = false;
    loadingHistory = false;
    unopenedCount = 0;

    private readonly destroy$ = new Subject<void>();
    private readonly openingNotificationIds = new Set<number>();

    constructor(private notificationService: NotificacionWebService,
                private socketIoService: SocketIoService,
                private notificationActionService: NotificacionWebActionService,
                private messageService: MessageService,
                public browserNotifications: BrowserNotificationService,
                public listas: ListasService) {
    }

    ngOnInit(): void {
        this.refreshUnopenedCount();
        this.loadNotificationTypes();
        this.socketIoService.onEvent(EventEnum.NOTIFICATION)
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => this.handleSocketNotification(event));
    }

    openRecent(event: Event, panel: OverlayPanel): void {
        this.loadRecent();
        panel.toggle(event);
    }

    openHistory(panel: OverlayPanel): void {
        panel.hide();
        this.resetHistoryFilters();
        this.historyVisible = true;
        this.loadHistory(0);
    }

    onHistoryPageChange(event: {page?: number}): void {
        this.loadHistory(event.page || 0);
    }

    onHistoryNotificationTypeChange(): void {
        this.loadHistory(0);
    }

    clearHistoryFilters(): void {
        this.resetHistoryFilters();
        this.loadHistory(0);
    }

    onHistoryDateChange(): void {
        if (!this.isValidDate(this.historyDateFrom) || !this.isValidDate(this.historyDateTo)) {
            return;
        }
        if (this.historyDateFrom > this.historyDateTo) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Filtro de fechas',
                detail: 'La fecha desde no puede ser posterior a la fecha hasta.'
            });
            return;
        }
        this.loadHistory(0);
    }

    notificationIcon(code: TipoNotificacionWeb): string {
        switch (code) {
            case 'PEDIDO_CATALOGO':
                return 'pi pi-shopping-cart';
            case 'PEDIDO_MENU':
                return 'pi pi-book';
            default:
                return 'pi pi-bell';
        }
    }

    notificationShortTitle(notification: NotificacionWeb): string {
        return notification.titulo || 'Nueva notificación';
    }

    trackByNotificationId(index: number, notification: NotificacionWeb): number {
        return notification.id;
    }

    get badgeText(): string {
        return this.unopenedCount > 99 ? '+99' : this.unopenedCount.toString();
    }

    markAsOpened(notification: NotificacionWeb): void {
        if (this.openingNotificationIds.has(notification.id)) {
            return;
        }
        if (notification.abierto) {
            if (this.requireUnreadNotificationToExecute && !this.canReopenNotification(notification)) {
                this.messageService.add({
                    severity: 'info',
                    summary: 'Notificación',
                    detail: 'Esta notificación ya fue abierta.'
                });
                return;
            }
            this.notificationActionService.execute(notification, () => this.closeNotificationPanels());
            return;
        }

        const previousUnopenedCount = this.unopenedCount;
        this.updateNotificationState(notification.id, true);
        this.unopenedCount = Math.max(0, this.unopenedCount - 1);
        this.openingNotificationIds.add(notification.id);
        this.notificationService.markAsOpened(notification.id)
            .pipe(finalize(() => this.openingNotificationIds.delete(notification.id)))
            .subscribe({
                next: response => {
                    if (!response.abiertaAhora) {
                        this.refreshUnopenedCount();
                    }
                    this.notificationActionService.execute(notification, () => this.closeNotificationPanels());
                },
                error: () => {
                    this.updateNotificationState(notification.id, false);
                    this.unopenedCount = previousUnopenedCount;
                    this.refreshUnopenedCount();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Notificación',
                        detail: 'No se pudo marcar la notificación como abierta.'
                    });
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadRecent(): void {
        this.loadingRecent = true;
        this.notificationService.findAll(0, this.recentPageSize).subscribe({
            next: page => {
                this.recentNotifications = page.content || [];
                this.unopenedCount = page.notificacionesSinAbrir || 0;
                this.loadingRecent = false;
            },
            error: () => this.loadingRecent = false
        });
    }

    private canReopenNotification(notification: NotificacionWeb): boolean {
        return notification.codigo === 'PEDIDO_MENU'
            || notification.codigo === 'PEDIDO_CATALOGO';
    }

    private loadNotificationTypes(): void {
        this.notificationService.findTypes().subscribe({
            next: types => this.historyNotificationTypes = types || []
        });
    }

    private loadHistory(pageNumber: number): void {
        const hasCompleteDateRange = this.isValidDate(this.historyDateFrom)
            && this.isValidDate(this.historyDateTo);
        this.loadingHistory = true;
        this.notificationService.findHistory(
            pageNumber,
            this.historyPageSize,
            this.historyNotificationType,
            hasCompleteDateRange ? this.historyDateFrom : undefined,
            hasCompleteDateRange ? this.historyDateTo : undefined
        ).subscribe({
            next: page => {
                this.historyNotifications = page.content || [];
                this.historyTotal = page.totalElements || 0;
                this.historyPage = page.number || 0;
                this.unopenedCount = page.notificacionesSinAbrir || 0;
                this.loadingHistory = false;
            },
            error: () => this.loadingHistory = false
        });
    }

    private isValidDate(date: Date): boolean {
        return date instanceof Date && !isNaN(date.getTime());
    }

    private resetHistoryFilters(): void {
        this.historyNotificationType = undefined;
        this.historyDateFrom = undefined;
        this.historyDateTo = undefined;
    }

    private updateNotificationState(id: number, abierto: boolean): void {
        [...this.recentNotifications, ...this.historyNotifications]
            .filter(notification => notification.id === id)
            .forEach(notification => {
                notification.abierto = abierto;
            });
    }

    private handleSocketNotification(event: any): void {
        const notification = (event?.value || event) as NotificacionWebSocket;
        if (!notification) {
            return;
        }
        if (notification.idEmpresa == null || !notification.codigo) {
            return;
        }
        if (notification.nueva === false) {
            this.loadRecent();
            if (this.historyVisible) {
                this.loadHistory(this.historyPage);
            }
            return;
        }
        this.playNotificationSound();
        this.showBrowserNotification(notification);
        this.refreshUnopenedCount();
    }

    enableBrowserNotifications(): void {
        this.browserNotifications.requestPermission().then(permission => {
            if (permission === 'granted') {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Notificaciones',
                    detail: 'Las alertas del navegador quedaron activadas.'
                });
            } else if (permission === 'denied') {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Notificaciones',
                    detail: 'El navegador bloqueó las alertas. Puedes habilitarlas desde la configuración del sitio.'
                });
            }
        });
    }

    private showBrowserNotification(notification: NotificacionWebSocket): void {
        if (document.visibilityState === 'visible' && document.hasFocus()) {
            return;
        }
        this.browserNotifications.show({
            title: notification.titulo || this.defaultBrowserNotificationTitle(notification.codigo),
            body: notification.mensaje || 'Tienes una nueva notificación en Teki.',
            tag: notification.idRelacionado ? 'teki-pedido-' + notification.idRelacionado : undefined,
            onClick: () => this.openBrowserNotificationTarget(notification)
        });
    }

    private defaultBrowserNotificationTitle(code: TipoNotificacionWeb): string {
        switch (code) {
            case 'PEDIDO_CATALOGO':
                return 'Pedido de catálogo';
            case 'PEDIDO_MENU':
                return 'Pedido de menú';
            default:
                return 'Nueva notificación';
        }
    }

    private openBrowserNotificationTarget(notification: NotificacionWebSocket): void {
        if (!notification.idRelacionado) {
            return;
        }
        this.notificationActionService.openPedidoById(notification.idRelacionado, () => this.closeNotificationPanels());
    }

    private playNotificationSound(): void {
        const audio = new Audio(this.notificationSoundUrl);
        audio.play().catch(() => {
            // El navegador puede bloquear audio hasta que exista interaccion del usuario.
        });
    }

    private closeNotificationPanels(): void {
        if (this.recentPanel) {
            this.recentPanel.hide();
        }
        this.historyVisible = false;
    }

    private refreshUnopenedCount(): void {
        this.notificationService.countUnopened().subscribe({
            next: response => {
                this.unopenedCount = response.notificacionesSinAbrir || 0;
            }
        });
    }
}
